/// <amd-module name="VSS/Features/SecurityUI/Roles/Components/RoleAssignmentToolbar" />
import * as React from "react";

import { VssComponent } from "VSS/Platform/Layout";

import { Button } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { FocusZone, FocusZoneDirection } from "VSSUI/FocusZone";
import { IMenuItem, MenuButton } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { css } from "VSSUI/Util";

import { RoleAssignmentsModel } from "VSS/Features/SecurityUI/Models";
import * as Resources from "VSS/Features/SecurityUI/Roles/Resources";

import { getService } from "../RolesManagementService";

export interface IRoleAssignmentToolbarProps {
    className?: string;
    disabled?: boolean;
    enableToggleInheritence?: boolean;
    model: RoleAssignmentsModel;
    onAddUserClick: () => void;
    resourceId: string;
    scopeId: string;
    serviceInstanceId?: string;
}

export class RoleAssignmentToolbar extends VssComponent<IRoleAssignmentToolbarProps> {
    public render(): JSX.Element {
        const { className, disabled, enableToggleInheritence, model, onAddUserClick } = this.props;

        let inheritanceMenuItems: IMenuItem[] = [
            {
                id: "inheritance-on",
                text: Resources.InheritanceOnActionText,
                checked: model.isInheritanceOn(),
                onActivate: this.onSetInheritanceOn
            },
            {
                id: "inheritance-off",
                text: Resources.InheritanceOffActionText,
                checked: model.isInheritanceOff(),
                onActivate: this.onSetInheritanceOff
            }
        ];

        return (
            <FocusZone direction={FocusZoneDirection.Horizontal}>
                <div className={css("security-role-toolbar flex-row bolt-default-horizontal-spacing", className)}>
                    <ButtonGroup role="toolbar">
                        <Button
                            ariaLabel={Resources.AddActionDescription}
                            disabled={disabled}
                            iconProps={{ iconName: "Add" }}
                            onClick={onAddUserClick}
                            subtle={true}
                        >
                            {Resources.AddActionText}
                        </Button>
                        <Observer dirty={model.isDirty()}>
                            {(props: { dirty: boolean }) => {
                                return (
                                    <>
                                        <Button
                                            ariaLabel={Resources.UndoActionDescription}
                                            disabled={disabled || !props.dirty}
                                            iconProps={{ iconName: "Undo" }}
                                            onClick={this.onUndoClick}
                                            subtle={true}
                                        >
                                            {Resources.UndoActionText}
                                        </Button>
                                        <Button
                                            ariaLabel={Resources.SaveActionDescription}
                                            disabled={disabled || !props.dirty}
                                            iconProps={{ iconName: "Save" }}
                                            onClick={this.onSaveClick}
                                            subtle={true}
                                        >
                                            {Resources.SaveActionText}
                                        </Button>
                                    </>
                                );
                            }}
                        </Observer>

                        {enableToggleInheritence && (
                            <MenuButton
                                ariaLabel={Resources.InheritanceActionDescription}
                                contextualMenuProps={{
                                    menuProps: {
                                        id: "inheritance",
                                        items: inheritanceMenuItems
                                    }
                                }}
                                disabled={disabled}
                                subtle={true}
                            >
                                {Resources.InheritanceActionText}
                            </MenuButton>
                        )}
                    </ButtonGroup>
                </div>
            </FocusZone>
        );
    }

    private onUndoClick = () => {
        const roleAssignmentService = getService(this.context.pageContext);
        roleAssignmentService.resetRoleAssignments(this.props.model);
    };

    private onSaveClick = () => {
        const { model, resourceId, scopeId, serviceInstanceId } = this.props;
        const { pageContext } = this.context;

        const roleAssignmentService = getService(pageContext);
        roleAssignmentService.saveRoleAssignments(pageContext, model, scopeId, resourceId, serviceInstanceId);
    };

    private onSetInheritanceOn = () => {
        const { model, resourceId, scopeId, serviceInstanceId } = this.props;
        const { pageContext } = this.context;

        const roleAssignmentService = getService(pageContext);
        roleAssignmentService.setInheritance(pageContext, model, true, scopeId, resourceId, serviceInstanceId);
    };

    private onSetInheritanceOff = () => {
        const { model, resourceId, scopeId, serviceInstanceId } = this.props;
        const { pageContext } = this.context;

        const roleAssignmentService = getService(pageContext);
        roleAssignmentService.setInheritance(pageContext, model, false, scopeId, resourceId, serviceInstanceId);
    };
}
